import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scroll-reveal for Orbit pages. Marks [data-reveal] elements .is-revealed
 * when they enter the viewport. Respects prefers-reduced-motion.
 */
export default function OrbitReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const nodes = Array.from(document.querySelectorAll('[data-reveal]'));

    if (!nodes.length) return undefined;

    if (reduce) {
      nodes.forEach((el) => el.classList.add('is-revealed'));
      return undefined;
    }

    const inView = (el) => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Reveal a bit before fully on screen; ignore tiny bottom sliver
      return r.top < vh * 0.92 && r.bottom > 48;
    };

    const reveal = (el) => el.classList.add('is-revealed');

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: '40px 0px -8% 0px' }
    );

    nodes.forEach((el) => {
      el.classList.remove('is-revealed');
      if (inView(el)) {
        // Already on screen (or mid-scroll) — don’t wait on IO edge cases
        reveal(el);
      } else {
        io.observe(el);
      }
    });

    // Safety: catch scroll/resize that IO can miss in some embeds
    const onScroll = () => {
      nodes.forEach((el) => {
        if (el.classList.contains('is-revealed')) return;
        if (!inView(el)) return;
        reveal(el);
        io.unobserve(el);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname]);

  return null;
}
